import type { CouponCampaignKind, CouponCardSize } from '../types';

export type { CouponCardSize };

/** Promotional coupon print sizes. */
export const PRIMARY_COUPON_CARD_SIZES: CouponCardSize[] = ['85.6/54', '98.5/67'];

/** Building unit resident cards — 86×54 mm avoids top/bottom gaps on physical stock. */
export const BUILDING_UNIT_CARD_SIZES: CouponCardSize[] = ['86/54'];

export const COUPON_CARD_SIZE_LABEL: Record<CouponCardSize, string> = {
  '85.6/54': '85.60 × 53.98 mm',
  '86/54': '86 × 54 mm',
  '98.5/67': '98.5 × 67 mm',
  '4/3': '4:3 (legacy)',
  '3/2': '3:2 (legacy)',
  '95/38': '95 × 38 mm (legacy)',
};

export interface MmSheetLayoutConfig {
  cardWidthMm: number;
  cardHeightMm: number;
  cols: number;
  colGapMm: number;
  rowGapMm: number;
  defaultRows: number;
}

const MM_SHEET_CONFIGS: Record<'85.6/54' | '86/54' | '98.5/67' | '95/38', MmSheetLayoutConfig> = {
  '85.6/54': {
    cardWidthMm: 85.6,
    cardHeightMm: 53.98,
    cols: 2,
    colGapMm: 10,
    rowGapMm: 2,
    defaultRows: 4,
  },
  '86/54': {
    cardWidthMm: 86,
    cardHeightMm: 54,
    cols: 2,
    colGapMm: 10,
    rowGapMm: 2,
    defaultRows: 4,
  },
  '98.5/67': {
    cardWidthMm: 98.5,
    cardHeightMm: 67,
    cols: 2,
    colGapMm: 10,
    rowGapMm: 2,
    defaultRows: 3,
  },
  '95/38': {
    cardWidthMm: 95,
    cardHeightMm: 38,
    cols: 2,
    colGapMm: 10,
    rowGapMm: 2,
    defaultRows: 5,
  },
};

export function defaultCardSizeForCampaignKind(kind?: CouponCampaignKind | null): CouponCardSize {
  return kind === 'building_unit' ? '86/54' : '85.6/54';
}

export function cardSizesForCampaignKind(kind?: CouponCampaignKind | null): CouponCardSize[] {
  return kind === 'building_unit' ? BUILDING_UNIT_CARD_SIZES : PRIMARY_COUPON_CARD_SIZES;
}

export function isMmSheetCardSize(size?: CouponCardSize | null): size is '85.6/54' | '86/54' | '98.5/67' | '95/38' {
  return size === '85.6/54' || size === '86/54' || size === '98.5/67' || size === '95/38';
}

export function getMmSheetLayoutConfig(size: CouponCardSize): MmSheetLayoutConfig | null {
  if (!isMmSheetCardSize(size)) return null;
  return MM_SHEET_CONFIGS[size];
}

/** CSS `aspect-ratio` value for template preview and non-grid print. */
export function cssAspectRatioForCardSize(size: CouponCardSize): string {
  switch (size) {
    case '85.6/54':
      return '85.6 / 53.98';
    case '86/54':
      return '86 / 54';
    case '98.5/67':
      return '98.5 / 67';
    case '95/38':
      return '95 / 38';
    case '3/2':
      return '3 / 2';
    default:
      return '4 / 3';
  }
}

/** Normalize legacy saved values when opening the editor. */
export function normalizeCouponCardSize(
  saved?: CouponCardSize | null,
  kind?: CouponCampaignKind | null,
): CouponCardSize {
  if (kind === 'building_unit') {
    if (saved === '86/54' || saved === '98.5/67') return saved;
    if (!saved || saved === '85.6/54' || saved === '4/3') return '86/54';
    return '86/54';
  }
  if (saved && isMmSheetCardSize(saved) && saved !== '86/54') return saved;
  if (saved === '3/2') return '98.5/67';
  if (saved === '4/3' || !saved) return '85.6/54';
  return saved;
}
